import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getDrillHistory, saveDrillAttempt } from '../lib/firestore';
import {
  DRILLS, BALL_HEX, BALL_POINTS,
  DRILL_TIER_CONFIGS, getDrillTier, getNextDrillTier, getDrillTierProgress,
} from '../shared';
import type { DrillDefinition, DrillAttempt, BallColor } from '../shared';
import { Panel, EmptyState, LoadingSpinner } from '../components/ui/Cards';
import { Button } from '../components/ui/Button';
import { DRILL_ICONS } from '../drillIcons';

const MATCH_STYLE_DRILLS = ['lineup', 'break_building'];
const BALLS: BallColor[] = ['red', 'yellow', 'green', 'brown', 'blue', 'pink', 'black'];

interface DrillSession {
  drill: DrillDefinition;
  startTime: number;
  currentBreak: number;
  breakSeq: BallColor[];
  highBreak: number;
  score: number;
  goodSafeties: number;
  totalSafeties: number;
}

const emptySession = (drill: DrillDefinition): DrillSession => ({
  drill, startTime: Date.now(),
  currentBreak: 0, breakSeq: [], highBreak: 0,
  score: 0, goodSafeties: 0, totalSafeties: 0,
});

export function DrillsPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [selectedDrill, setSelectedDrill] = useState<DrillDefinition | null>(null);
  const [history, setHistory] = useState<DrillAttempt[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeDrill, setActiveDrill] = useState<DrillSession | null>(null);
  const [showBallPicker, setShowBallPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const breakValRef = useRef<HTMLDivElement>(null);

  const selectDrill = async (drill: DrillDefinition) => {
    setSelectedDrill(drill);
    setLoadingHistory(true);
    if (user) setHistory(await getDrillHistory(user.uid, drill.id));
    setLoadingHistory(false);
  };

  const startDrill = () => {
    if (!selectedDrill) return;
    setActiveDrill(emptySession(selectedDrill));
    setShowBallPicker(false);
  };

  const recordPot = (ball: BallColor) => {
    setShowBallPicker(false);
    setActiveDrill(d => {
      if (!d) return d;
      const points = BALL_POINTS[ball];
      const newBreak = d.currentBreak + points;
      if (breakValRef.current) {
        breakValRef.current.classList.remove('logger__break-val--pulse');
        void breakValRef.current.offsetWidth;
        breakValRef.current.classList.add('logger__break-val--pulse');
      }
      return { ...d, currentBreak: newBreak, breakSeq: [...d.breakSeq, ball], highBreak: Math.max(d.highBreak, newBreak) };
    });
  };

  const recordMiss = async () => {
    if (!activeDrill || !user) return;
    const finalHigh = Math.max(activeDrill.highBreak, activeDrill.currentBreak);
    if (activeDrill.drill.id === 'lineup') {
      await saveDrillResult({ ...activeDrill, highBreak: finalHigh, currentBreak: 0 });
    } else {
      setActiveDrill(d => d ? { ...d, currentBreak: 0, breakSeq: [], highBreak: finalHigh } : d);
    }
  };

  const saveDrillResult = async (session: DrillSession) => {
    if (!user) return;
    setSaving(true);
    const config = DRILL_TIER_CONFIGS[session.drill.id];
    const duration = Math.round((Date.now() - session.startTime) / 1000);

    const finalScore = MATCH_STYLE_DRILLS.includes(session.drill.id)
      ? session.highBreak
      : config.scoreType === 'safety_pct'
        ? (session.totalSafeties > 0 ? Math.round((session.goodSafeties / session.totalSafeties) * 100) : 0)
        : session.score;

    await saveDrillAttempt({
      uid: user.uid,
      drillId: session.drill.id,
      date: new Date(),
      successRate: Math.round((finalScore / config.maxScore) * 100),
      potsMade: finalScore,
      potsAttempted: finalScore,
      durationSeconds: duration,
      notes: String(finalScore),
    });

    setHistory(await getDrillHistory(user.uid, session.drill.id));
    setActiveDrill(null);
    setShowBallPicker(false);
    setSaving(false);
  };

  // ── Active drill ───────────────────────────────────────────────
  if (activeDrill) {
    const config = DRILL_TIER_CONFIGS[activeDrill.drill.id];
    const isMatchStyle = MATCH_STYLE_DRILLS.includes(activeDrill.drill.id);
    const displayScore = isMatchStyle ? activeDrill.highBreak : activeDrill.score;
    const currentTier = getDrillTier(activeDrill.drill.id, displayScore);
    const nextTier = getNextDrillTier(activeDrill.drill.id, displayScore);
    const DrillIcon = DRILL_ICONS[activeDrill.drill.id];

    return (
      <div className="active-drill">
        <div className="active-drill__header">
          <div className="active-drill__icon">
            <DrillIcon size={32} strokeWidth={1.5} color="var(--gold)" />
          </div>
          <h2>{activeDrill.drill.name}</h2>
          <p>{activeDrill.drill.description}</p>
        </div>

        {currentTier && (
          <div style={{ textAlign: 'center', marginBottom: 14 }}>
            <span style={{ display: 'inline-block', padding: '4px 14px', borderRadius: 20, background: currentTier.color + '22', border: `1px solid ${currentTier.color}`, color: currentTier.color, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {currentTier.label}
            </span>
          </div>
        )}

        {/* Ball-by-ball: lineup + break_building */}
        {isMatchStyle && (
          <>
            <div className="logger__break-display" style={{ marginBottom: 14 }}>
              <div className="logger__break-label">{t('drills.currentBreak')}</div>
              <div ref={breakValRef} className="logger__break-val">{activeDrill.currentBreak}</div>
              <div className="logger__break-seq">
                {activeDrill.breakSeq.map((ball, i) => (
                  <div key={i} className="logger__ball-dot" style={{ background: BALL_HEX[ball], color: ball === 'yellow' ? 'rgba(0,0,0,0.7)' : '#fff' }}>
                    {BALL_POINTS[ball]}
                  </div>
                ))}
              </div>
            </div>

            {activeDrill.highBreak > 0 && (
              <div style={{ textAlign: 'center', marginBottom: 12 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--chalk-faint)', letterSpacing: '0.1em' }}>
                  {t('drills.highBreak').toUpperCase()}: <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{activeDrill.highBreak}</span>
                </span>
              </div>
            )}

            {showBallPicker ? (
              <div className="logger__ball-picker">
                <div className="logger__ball-picker-label">{t('session.whichBall')}</div>
                <div className="logger__ball-picker-row">
                  {BALLS.map(ball => (
                    <button key={ball} className="logger__ball-btn" onClick={() => recordPot(ball)}
                      style={{ background: BALL_HEX[ball], color: ball === 'yellow' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)' }}>
                      {BALL_POINTS[ball]}
                    </button>
                  ))}
                </div>
                <div className="logger__ball-picker-cancel">
                  <button className="logger__cancel-btn" onClick={() => setShowBallPicker(false)}>{t('session.cancel')}</button>
                </div>
              </div>
            ) : (
              <div className="logger__shot-grid" style={{ marginBottom: 12 }}>
                <button className="logger__shot-btn logger__shot-btn--pot" onClick={() => setShowBallPicker(true)}>
                  {t('session.pot')}
                </button>
                <button className="logger__shot-btn logger__shot-btn--miss" onClick={recordMiss}>
                  {activeDrill.drill.id === 'lineup' ? t('drills.missEndDrill') : t('session.miss')}
                </button>
              </div>
            )}

            <div className="drill-actions">
              <Button variant="outline" fullWidth onClick={() => { setActiveDrill(null); setShowBallPicker(false); }}>Cancel</Button>
              {activeDrill.drill.id === 'break_building' && (
                <Button fullWidth loading={saving} onClick={() => saveDrillResult(activeDrill)} disabled={activeDrill.highBreak === 0}>
                  {t('drills.finish')}
                </Button>
              )}
            </div>
          </>
        )}

        {/* Simple count: long_pot, colors_clearance, baulk_colors */}
        {!isMatchStyle && config.scoreType !== 'safety_pct' && (
          <>
            <div className="drill-counters">
              <div className="drill-counter">
                <div className="drill-counter__val" style={{ color: currentTier?.color ?? 'var(--gold)' }}>{activeDrill.score}</div>
                <div className="drill-counter__label">{config.scoreLabel}</div>
              </div>
              {nextTier && (
                <div className="drill-counter">
                  <div className="drill-counter__val" style={{ fontSize: '1rem', color: nextTier.color }}>{nextTier.minScore}</div>
                  <div className="drill-counter__label">{t('drills.nextTier')}</div>
                </div>
              )}
            </div>
            <div className="drill-btn-grid">
              <button className="drill-btn--pot" onClick={() => setActiveDrill(d => d ? { ...d, score: d.score + 1 } : d)}>✓ Pot</button>
              <button className="drill-btn--miss" onClick={() => saveDrillResult(activeDrill)}>{t('drills.missEndDrill')}</button>
            </div>
            <div className="drill-actions">
              <Button variant="outline" fullWidth onClick={() => setActiveDrill(null)}>Cancel</Button>
            </div>
          </>
        )}

        {/* Safety exchange */}
        {config.scoreType === 'safety_pct' && (
          <>
            <div className="drill-counters">
              <div className="drill-counter">
                <div className="drill-counter__val" style={{ color: currentTier?.color ?? 'var(--gold)' }}>
                  {activeDrill.totalSafeties > 0 ? `${Math.round((activeDrill.goodSafeties / activeDrill.totalSafeties) * 100)}%` : '—'}
                </div>
                <div className="drill-counter__label">{t('drills.goodSafetyPct')}</div>
              </div>
              <div className="drill-counter">
                <div className="drill-counter__val">{activeDrill.goodSafeties}</div>
                <div className="drill-counter__label">{t('drills.good')}</div>
              </div>
              <div className="drill-counter">
                <div className="drill-counter__val">{activeDrill.totalSafeties}</div>
                <div className="drill-counter__label">{t('drills.total')}</div>
              </div>
            </div>
            <div className="drill-btn-grid">
              <button className="drill-btn--pot" onClick={() => setActiveDrill(d => d ? { ...d, goodSafeties: d.goodSafeties + 1, totalSafeties: d.totalSafeties + 1 } : d)}>{t('drills.goodSafety')}</button>
              <button className="drill-btn--miss" onClick={() => setActiveDrill(d => d ? { ...d, totalSafeties: d.totalSafeties + 1 } : d)}>{t('drills.poorSafety')}</button>
            </div>
            <div className="drill-actions">
              <Button variant="outline" fullWidth onClick={() => setActiveDrill(null)}>Cancel</Button>
              <Button fullWidth loading={saving} onClick={() => saveDrillResult(activeDrill)} disabled={activeDrill.totalSafeties === 0}>{t('drills.finish')}</Button>
            </div>
          </>
        )}

        {/* Tier benchmarks */}
        <div style={{ marginTop: 16, padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: 'var(--border-subtle)' }}>
          <div style={{ fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--chalk-faint)', marginBottom: 8 }}>{t('drills.tierBenchmarks')}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {config.tiers.map(tier => {
              const reached = displayScore >= tier.minScore;
              return (
                <div key={tier.level} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: reached ? tier.color : 'var(--chalk-faint)' }}>
                    {tier.minScore}{config.scoreType === 'safety_pct' ? '%' : ''}
                  </div>
                  <div style={{ fontSize: '0.58rem', color: reached ? tier.color : 'var(--chalk-faint)', marginTop: 2, fontWeight: 600 }}>
                    {tier.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Drill selection ────────────────────────────────────────────
  return (
    <div className="page u-fade-up">
      <div className="page__header">
        <div>
          <h1 className="page__title">{t('drills.title')}</h1>
          <p className="page__sub">{t('drills.sub')}</p>
        </div>
      </div>

      <div className="drill-grid">
        {DRILLS.map(drill => {
          const config = DRILL_TIER_CONFIGS[drill.id];
          const isMatchStyle = MATCH_STYLE_DRILLS.includes(drill.id);
          const DrillIcon = DRILL_ICONS[drill.id];
          return (
            <button
              key={drill.id}
              className={`drill-card${selectedDrill?.id === drill.id ? ' drill-card--active' : ''}`}
              onClick={() => selectDrill(drill)}
            >
              <div className="drill-card__icon">
                <DrillIcon size={22} strokeWidth={1.5} color="var(--chalk-dim)" />
              </div>
              <div className="drill-card__name">{drill.name}</div>
              <div className="drill-card__desc">{drill.description}</div>
              <div style={{ fontSize: '0.65rem', color: isMatchStyle ? 'var(--gold-dim)' : 'var(--chalk-faint)', fontWeight: 600, marginBottom: 10 }}>
                {isMatchStyle ? t('drills.ballByBall') : `${t('drills.scoresBy')} ${config.scoreLabel}`}
              </div>
              <div className="drill-card__balls">
                {drill.ballsInvolved.slice(0, 5).map((ball, i) => (
                  <div key={i} className="drill-card__ball" style={{ background: BALL_HEX[ball] }} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
                {config.tiers.map(tier => (
                  <div key={tier.level} style={{ flex: 1, height: 3, borderRadius: 2, background: tier.color + '66' }} />
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {selectedDrill && (
        <Panel
          title={`${selectedDrill.name} — ${t('drills.history')}`}
          action={<Button size="sm" onClick={startDrill}>{t('drills.startDrill')}</Button>}
        >
          {loadingHistory ? (
            <LoadingSpinner size={24} />
          ) : history.length === 0 ? (
            <EmptyState icon="" title={t('drills.noAttempts')} subtitle={t('drills.noAttemptsSub')} />
          ) : (
            <DrillHistoryPanel drill={selectedDrill} history={history} t={t} />
          )}
        </Panel>
      )}
    </div>
  );
}

function DrillHistoryPanel({ drill, history, t }: { drill: DrillDefinition; history: DrillAttempt[]; t: (k: string) => string }) {
  const config = DRILL_TIER_CONFIGS[drill.id];
  const rawScores = history.map(h => Number(h.notes ?? 0)).filter(n => !isNaN(n));
  const bestScore = rawScores.length > 0 ? Math.max(...rawScores) : 0;
  const bestTier = getDrillTier(drill.id, bestScore);
  const nextTier = getNextDrillTier(drill.id, bestScore);
  const progress = getDrillTierProgress(drill.id, bestScore);

  return (
    <>
      <div style={{ display: 'flex', gap: 14, marginBottom: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 100, padding: '14px', background: 'var(--glass-bg)', border: 'var(--border-card)', borderRadius: 8, textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 800, color: bestTier?.color ?? 'var(--gold)', lineHeight: 1, letterSpacing: '-0.02em' }}>
            {bestScore}{config.scoreType === 'safety_pct' ? '%' : ''}
          </div>
          <div style={{ fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--chalk-faint)', marginTop: 4 }}>
            {t('drills.personalBest')} — {config.scoreLabel}
          </div>
        </div>
        {bestTier && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ display: 'inline-block', padding: '6px 16px', borderRadius: 20, background: bestTier.color + '22', border: `1px solid ${bestTier.color}`, color: bestTier.color, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
              {bestTier.label}
            </div>
            {nextTier && (
              <div>
                <div style={{ fontSize: '0.65rem', color: 'var(--chalk-faint)', marginBottom: 4 }}>
                  {progress}% toward {nextTier.label} ({nextTier.minScore}{config.scoreType === 'safety_pct' ? '%' : ''})
                </div>
                <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden', width: 140 }}>
                  <div style={{ height: '100%', width: `${progress}%`, background: `linear-gradient(to right, ${bestTier.color}88, ${nextTier.color})`, borderRadius: 4, transition: 'width 1s ease' }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {config.tiers.map(tier => {
          const reached = bestScore >= tier.minScore;
          return (
            <div key={tier.level} style={{ flex: 1, padding: '10px 8px', borderRadius: 6, background: reached ? `${tier.color}18` : 'rgba(255,255,255,0.02)', border: `1px solid ${reached ? tier.color + '66' : 'rgba(255,255,255,0.05)'}`, textAlign: 'center' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: reached ? tier.color : 'var(--chalk-faint)' }}>
                {tier.minScore}{config.scoreType === 'safety_pct' ? '%' : ''}
              </div>
              <div style={{ fontSize: '0.58rem', fontWeight: 600, color: reached ? tier.color : 'var(--chalk-faint)', marginTop: 2 }}>
                {reached ? '✓ ' : ''}{tier.label}
              </div>
            </div>
          );
        })}
      </div>

      {history.slice(0, 10).map((attempt, i) => {
        const rawScore = Number(attempt.notes ?? 0);
        const tier = getDrillTier(drill.id, rawScore);
        return (
          <div key={attempt.id} className="drill-history-item">
            <div className="drill-history-item__date">
              {attempt.date
                ? new Date((attempt.date as any).seconds * 1000).toLocaleDateString('en-GB')
                : `Attempt ${history.length - i}`}
            </div>
            <div className="drill-history-item__stats">
              <span style={{ color: tier?.color ?? 'var(--chalk-faint)', fontWeight: 600 }}>
                {rawScore}{config.scoreType === 'safety_pct' ? '%' : ''}
              </span>
              {tier && <span style={{ color: tier.color, fontSize: '0.62rem' }}>{tier.label}</span>}
            </div>
          </div>
        );
      })}
    </>
  );
}